import { trainingDecisionSchema, type TrainingDecision } from '@cadencia/contracts';
import {
  OUTCOME_EXPOSURE_WEEKS,
  evaluateOutcome,
  findExercise,
  volumeObservations,
  type Outcome,
  type VolumeEpisode,
  type VolumeObservation,
} from '@cadencia/domain';
import { logger } from '../../lib/logger.js';
import { addDays, diaLocalDe, parseLocalDate, todayLocal } from '../../lib/dates.js';
import { db } from '../db.js';
import { nowIso } from '../ids.js';
import { listSessionsOfDateRange } from '../repositories/trainingRepo.js';
import { putRow, type WriteContext } from '../writes.js';

/**
 * Evaluar lo que se aplicó, y derivar la memoria del atleta (E5).
 *
 * `training_decisions.evaluate_after`, `outcome` y `outcome_evaluated_at`
 * existían desde la Fase 3 y se escribían SIEMPRE en `null`. Desde el
 * 22/09/2026 `applyDecisions` fija `evaluateAfter` al aplicar un cambio de
 * volumen, y esto es lo que, llegada esa fecha, mira qué pasó.
 *
 * IDEMPOTENTE, y tiene que serlo: se llama al cerrar cada semana —a mano o
 * sola, en `weekCloseService`—, y una decisión
 * con `outcomeEvaluatedAt` ya puesto no se vuelve a juzgar. Si se reevaluara en
 * cada apertura, el veredicto podría cambiar con datos que llegan después —una
 * sesión sincronizada tarde— y la memoria del atleta diría cosas distintas
 * según el día en que se mirara.
 */

/** Series de rendimiento de un músculo en una ventana, por ejercicio y semana. */
async function rendimientoPorEjercicio(
  userId: string,
  muscle: string,
  desde: string,
  hasta: string,
): Promise<Map<string, Map<string, number[]>>> {
  // Acotado por `performedAt`, que está indexado. El límite inferior es exacto:
  // `parseLocalDate` da la medianoche LOCAL, que es el primer instante que puede
  // tener una serie de ese día local.
  const series = (
    await db.performedSets.where('performedAt').aboveOrEqual(parseLocalDate(desde).toISOString()).toArray()
  ).filter((fila) => {
    if (fila.userId !== userId || fila.deletedAt !== null) return false;
    const dia = diaLocalDe(fila.performedAt);
    return dia >= desde && dia < hasta;
  });

  const porEjercicio = new Map<string, Map<string, number[]>>();
  for (const fila of series) {
    const ejercicio = findExercise(fila.exerciseSlug);
    if (ejercicio === undefined || !ejercicio.primaryMuscles.includes(muscle as never)) continue;
    const semana = semanaDe(diaLocalDe(fila.performedAt), desde);
    const deEste = porEjercicio.get(fila.exerciseSlug) ?? new Map<string, number[]>();
    deEste.set(semana, [...(deEste.get(semana) ?? []), fila.e1rmKg]);
    porEjercicio.set(fila.exerciseSlug, deEste);
  }
  return porEjercicio;
}

/** Índice de semana, contado desde el inicio de la ventana. */
function semanaDe(dia: string, inicio: string): string {
  const dias = Math.floor(
    (Date.parse(`${dia}T00:00:00Z`) - Date.parse(`${inicio}T00:00:00Z`)) / 86_400_000,
  );
  return String(Math.floor(dias / 7));
}

/**
 * e1RM medio por semana, usando SOLO los ejercicios comunes a las dos ventanas.
 *
 * Es la exigencia del §10 hecha cuenta: «compara rendimientos bajo condiciones
 * razonablemente equivalentes». Promediar un press de banca antes con una
 * máquina después mediría el cambio de ejercicio, no la respuesta al volumen.
 */
function mediaSemanal(
  porEjercicio: Map<string, Map<string, number[]>>,
  comunes: ReadonlySet<string>,
  semanas: number,
): number[] {
  const salida: number[] = [];
  for (let s = 0; s < semanas; s += 1) {
    const valores: number[] = [];
    for (const [slug, porSemana] of porEjercicio) {
      if (!comunes.has(slug)) continue;
      const deLaSemana = porSemana.get(String(s)) ?? [];
      if (deLaSemana.length > 0) valores.push(Math.max(...deLaSemana));
    }
    salida.push(valores.length === 0 ? 0 : valores.reduce((a, b) => a + b, 0) / valores.length);
  }
  return salida;
}

async function juzgar(userId: string, decision: TrainingDecision): Promise<Outcome & {
  muscle: string;
  deltaSets: number;
}> {
  const muscle = String((decision.inputs as { muscle?: unknown }).muscle ?? '');
  const antes = Number(decision.previousValue ?? 0);
  const despues = Number(decision.newValue ?? 0);
  const aplicada = diaLocalDe(decision.appliedAt ?? decision.decidedAt);

  const semanas = OUTCOME_EXPOSURE_WEEKS;
  const inicioAntes = addDays(aplicada, -semanas * 7);
  const finDespues = addDays(aplicada, semanas * 7);

  const [previo, posterior] = await Promise.all([
    rendimientoPorEjercicio(userId, muscle, inicioAntes, aplicada),
    rendimientoPorEjercicio(userId, muscle, aplicada, finDespues),
  ]);

  const comunes = new Set([...previo.keys()].filter((slug) => posterior.has(slug)));

  const sesiones = (await listSessionsOfDateRange(userId, aplicada, finDespues)).filter(
    (s) => s.sessionKind === 'strength' || s.sessionKind === 'strength_plus_cardio',
  );
  const pasadas = sesiones.filter((s) => s.status !== 'planned');
  const hechas = pasadas.filter((s) => s.status === 'completed').length;

  const semanasDeDescarga = (await db.trainingWeeks.where('userId').equals(userId).toArray()).filter(
    (w) =>
      w.deletedAt === null &&
      w.intent === 'deload' &&
      w.weekStart >= aplicada &&
      w.weekStart < finDespues,
  );

  const resultado = evaluateOutcome({
    beforeWeeklyE1rm: mediaSemanal(previo, comunes, semanas),
    afterWeeklyE1rm: mediaSemanal(posterior, comunes, semanas),
    adherenceAfter: pasadas.length === 0 ? null : hechas / pasadas.length,
    exercisesChanged: comunes.size === 0 && (previo.size > 0 || posterior.size > 0),
    deloadInWindow: semanasDeDescarga.length > 0,
  });

  return { ...resultado, muscle, deltaSets: despues - antes };
}

/**
 * Evalúa las decisiones cuyo momento ha llegado. Devuelve cuántas se juzgaron.
 *
 * No lanza por una decisión rara: la registra y sigue. Una sola fila con un
 * `newValue` inesperado no puede dejar a todas las demás sin evaluar.
 */
export async function evaluateDueDecisions(ctx: WriteContext, hoy = todayLocal()): Promise<number> {
  const pendientes = (
    await db.trainingDecisions.where('userId').equals(ctx.userId).toArray()
  ).filter(
    (fila) =>
      fila.deletedAt === null &&
      fila.appliedAt !== null &&
      fila.evaluateAfter !== null &&
      fila.evaluateAfter <= hoy &&
      fila.outcomeEvaluatedAt === null,
  );

  let juzgadas = 0;
  for (const fila of pendientes) {
    try {
      const outcome = await juzgar(ctx.userId, fila);
      await putRow(
        ctx,
        'trainingDecisions',
        trainingDecisionSchema.parse({
          ...fila,
          outcome,
          outcomeEvaluatedAt: nowIso(),
        }),
      );
      juzgadas += 1;
    } catch (error) {
      logger.warn('engine', 'OUTCOME_EVALUATION_FAILED', 'No se pudo evaluar una decisión', {
        tipo: fila.decisionType,
        detalle: error instanceof Error ? error.message : String(error),
      });
    }
  }
  return juzgadas;
}

/** Los cambios de volumen ya evaluados, en la forma que la memoria entiende. */
export async function volumeEpisodes(userId: string): Promise<VolumeEpisode[]> {
  const filas = (await db.trainingDecisions.where('userId').equals(userId).toArray()).filter(
    (fila) =>
      fila.deletedAt === null &&
      fila.decisionType === 'volume_change' &&
      fila.outcomeEvaluatedAt !== null,
  );

  const salida: VolumeEpisode[] = [];
  for (const fila of filas) {
    const outcome = fila.outcome as Partial<Outcome & { muscle: string; deltaSets: number }> | null;
    if (outcome?.verdict === undefined || outcome.muscle === undefined) continue;
    salida.push({
      muscle: outcome.muscle,
      deltaSets: Number(outcome.deltaSets ?? 0),
      verdict: outcome.verdict,
      evaluatedOn: diaLocalDe(fila.outcomeEvaluatedAt as string),
    });
  }
  return salida;
}

/** La memoria del atleta: derivada cada vez, así que no puede quedarse vieja. */
export async function athleteMemory(
  userId: string,
  hoy = todayLocal(),
): Promise<VolumeObservation[]> {
  return volumeObservations(await volumeEpisodes(userId), hoy);
}
